<template>
	<div class="edit-row edit-row--b-top">
		<div class="edit-row__label">{{ t('sidebar.edgeDesign.label.center') }}</div>
		<field-regular
			:placeholder="t('sidebar.edgeDesign.placeholder.text')"
			:label-inline="true"
			:field-item-grow="true"
			:field-item-classname="'mod--edit-bar'"
			:disabled="blocked"
			:return-null-if-empty="false"
			:allow-slot="isI18nAvailable"
			v-model="edgeContentMiddle"
			@on-change="updateLang($event, 'middle')"
		>
			<i
				@click="openDesignSettings"
				class="bg--golden color--black ml--10 pv--1 ph--5 cursor--pointer fas fa-language"
			></i>
		</field-regular>
	</div>
	<div class="edit-row edit-row--b-top">
		<field-regular
			:label="t('quickEditBar.textStart')"
			:placeholder="t('sidebar.edgeDesign.placeholder.text')"
			:label-inline="true"
			:field-item-grow="true"
			:field-item-classname="'mod--edit-bar'"
			:disabled="blocked"
			:return-null-if-empty="false"
			:allow-slot="isI18nAvailable"
			v-model="edgeContentStart"
			@on-change="updateLang($event, 'start')"
		>
			<i
				@click="openDesignSettings"
				class="bg--golden color--black ml--10 pv--1 ph--5 cursor--pointer fas fa-language"
			></i>
		</field-regular>
		<field-regular
			:label="t('quickEditBar.textEnd')"
			:placeholder="t('sidebar.edgeDesign.placeholder.text')"
			:label-inline="true"
			:field-item-grow="true"
			:field-item-classname="'mod--edit-bar'"
			:disabled="blocked"
			:return-null-if-empty="false"
			:allow-slot="isI18nAvailable"
			v-model="edgeContentEnd"
			@on-change="updateLang($event, 'end')"
		>
			<i
				@click="openDesignSettings"
				class="bg--golden color--black ml--10 pv--1 ph--5 cursor--pointer fas fa-language"
			></i>
		</field-regular>
	</div>
</template>

<script>
import FieldRegular from '@/components/fields/FieldRegular';
import {computed, inject, watch} from 'vue';
import {useStore} from 'vuex';
import {useI18n} from 'vue-i18n';
import {DASHBOARD_OPTIONS} from "@/models/DASHBOARD_OPTIONS";
import useTranslations from "@/utils/language/useTranslations";
export default {
	components: {FieldRegular},
	props: {
		blocked: {
			type: Boolean,
			default: false
		}
	},
	setup(props, {emit}) {
		const {t} = useI18n();
		const store = useStore();
		const emitter = inject('emitter');
		const language = computed(() => store.getters["maps/languages/getLanguage"].id);
		const currentLang = computed(() => store.getters["maps/languages/getLanguageLocal"].id);
		const edge = computed(() => store.getters["edges/getSelectedEdge"]);
		const edgesOriginal = computed(() => store.getters["edges/getRawEdges"]);
		const edgeOriginal = computed(() => store.getters["edges/getSelectedRawEdge"])
		const edgeActiveID = edgeOriginal.value.id;
		
		const edgeContentStart = computed({
			get(){return edge.value.content.start},
			set(value){
				const edgeData = Object.assign({}, edgeOriginal.value.content.lang[currentLang.value], {start: value});
				delete edgeData.translated;
				const result = {lang: {[currentLang.value]: edgeData}};
				edgeContentUpdate(result);
				edgeContentSave(result);
			}
		})
		const edgeContentMiddle = computed({
			get(){return edge.value.content.middle},
			set(value){
				const edgeData = Object.assign({}, edgeOriginal.value.content.lang[currentLang.value], {middle: value});
				delete edgeData.translated;
				const result = {lang: {[currentLang.value]: edgeData}};
				edgeContentUpdate(result);
				edgeContentSave(result);
			}
		})
		const edgeContentEnd = computed({
			get(){return edge.value.content.end},
			set(value){
				const edgeData = Object.assign({}, edgeOriginal.value.content.lang[currentLang.value], {end: value});
				delete edgeData.translated;
				const result = {lang: {[currentLang.value]: edgeData}};
				edgeContentUpdate(result);
				edgeContentSave(result);
			}
		})
		
		// ACTIONS
		function edgeContentUpdate(edgeData, id){
			if(id === undefined) id = edge.value.id;
			store.dispatch('edges/content/edgeContentUpdate', {id, edgeData});
		}
		function edgeContentSave(edgeData, id){
			if(id === undefined) id = edge.value.id;
			store.dispatch('edges/content/edgeContentSave', {id, edgeData});
		}
		
		// i18n settings
		const clientFeatures = computed(() => store.getters["clients/getClientFeatures"]);
		const isI18nAvailable = computed(() => clientFeatures.value.has_i18n);
		const EDGE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.EDGE.EDGE_DESIGN;
		async function openDesignSettings(){
			await store.dispatch("dashboardOptions/optionChoose", EDGE_DESIGN);
			// TODO replace with store actions, because at first call while component is rendered asynchronously
			//  event emitted nowhere
			emitter.emit('panelOpen', 2);
			emitter.emit('closeQuickEditBarPanel');
		}
		
		// language
		const {getSubvariants} = useTranslations();
		function updateLang(value, field){
			// replace edgeOriginal getting from store in case edit-bar is closed when this function fired
			const edgeOriginal = edgesOriginal.value.find(edge => edge.id === edgeActiveID);
			if(currentLang.value === language.value){
				const edgeContent = Object.assign({}, edgeOriginal.content.lang[currentLang.value], {[field]: value});
				delete edgeContent.translated;
				const result = {lang: {[currentLang.value]: edgeContent}}
				for(const [lang, object] of Object.entries(edgeOriginal.content.lang)){
					if(lang !== currentLang.value && !object[field] && !object.translated){
						// make destructured copy
						const content = Object.assign({}, {[lang]: {...object}});
						content[lang][field] = value;
						delete content[lang].translated;
						// assign to the result object
						Object.assign(result.lang, content);
					}
				}
				
				if(Object.keys(result.lang).length > 1){
					edgeContentUpdate(result, edgeActiveID);
					edgeContentSave(result, edgeActiveID);
				}
				return;
			}
			// if the current lang isn't a default language,
			// check if there are subvariants of the current language and update them accordingly
			const subvariants = getSubvariants({id: currentLang.value, lang: edgeOriginal.content.lang});
			if(subvariants.length && currentLang.value !== language.value){
				const edgeContent = Object.assign({}, edgeOriginal.content.lang[currentLang.value], {[field]: value});
				delete edgeContent.translated;
				const result = {lang: {[currentLang.value]: edgeContent}}
				subvariants.forEach(lang => {
					if(!edgeOriginal.content.lang[lang][field] && !edgeOriginal.content.lang[lang].translated) {
						const updatedContent = Object.assign({}, edgeOriginal.content.lang[lang], {[field]: value});
						delete updatedContent.translated;
						Object.assign(result.lang, {[lang]: {...updatedContent}})
					}
				})
				edgeContentUpdate(result, edgeActiveID);
				edgeContentSave(result, edgeActiveID);
			}
		}
		
		return {
			t,
			edgeContentStart,
			edgeContentMiddle,
			edgeContentEnd,
			openDesignSettings,
			isI18nAvailable,
			updateLang,
		}
	}
}
</script>

<style lang="scss">

</style>
